import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "../../../components/layout/DashboardLayout";
import AceAvatar from "../../../components/shared/AceAvatar";

import {
  fetchChallenges,
  fetchTips,
  CONTENT_STATUS,
  CONTENT_CATEGORIES,
} from "../../../services/contentService";

function Chip({ label, value }) {
  return (
    <div className="ac-content-chip">
      <span className="ac-content-chip-value">{value}</span>
      <span className="ac-content-chip-label">{label}</span>
    </div>
  );
}

function ContentGlyph({ type }) {
  if (type === "tips") {
    return (
      <svg viewBox="0 0 24 24" className="ac-content-glyph" aria-hidden="true">
        <path
          d="M12 4.5a5.2 5.2 0 0 1 3.8 8.8c-.8.8-1.3 1.6-1.5 2.4h-4.6c-.2-.8-.7-1.6-1.5-2.4A5.2 5.2 0 0 1 12 4.5Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M10 18h4M10.5 20h3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className="ac-content-glyph" aria-hidden="true">
      <path
        d="M7 6.5h10v2.2c0 2.4-1.2 4.6-3.2 6l-1.8 1.3-1.8-1.3A7.3 7.3 0 0 1 7 8.7V6.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M9.5 4.5h5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M10 10.8l1.3 1.3 2.8-2.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Card({ title, desc, count, type, cta, onClick, tone = "green" }) {
  return (
    <div className={`ac-soft-panel ac-content-maincard ${tone === "blue" ? "is-blue" : "is-green"}`}>
      <div className="ac-content-maincard-head">
        <div className="ac-content-maincard-copy">
          <h3 className="ac-content-maincard-title">{title}</h3>
          <p className="ac-content-maincard-text">{desc}</p>
          <div className="ac-content-inline-meta">
            <span className="ac-content-inline-badge">{count} Einträge</span>
          </div>
        </div>

        <div className="ac-content-maincard-icon">
          <ContentGlyph type={type} />
        </div>
      </div>

      <button onClick={onClick} className="ac-ghostbtn ac-content-smallbtn">
        {cta}
      </button>
    </div>
  );
}

function QuickTile({ title, sub, type, onClick }) {
  return (
    <button onClick={onClick} className="ac-content-quicktile">
      <div className="ac-content-quicktile-top">
        <div className="ac-content-quicktile-icon">
          <ContentGlyph type={type} />
        </div>
      </div>

      <div className="ac-content-quicktile-title">{title}</div>
      <div className="ac-content-quicktile-text">{sub}</div>
    </button>
  );
}

export default function ContentHome() {
  const navigate = useNavigate();

  const [tips, setTips] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const [tipsData, challengesData] = await Promise.all([
          fetchTips(),
          fetchChallenges(),
        ]);

        if (!mounted) return;
        setTips(tipsData);
        setChallenges(challengesData);
      } catch (error) {
        console.error("Content konnte nicht geladen werden:", error);
        if (!mounted) return;
        setTips([]);
        setChallenges([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const stats = useMemo(() => {
    const all = [...tips, ...challenges];

    return {
      tipCount: tips.length,
      challengeCount: challenges.length,
      drafts: all.filter((x) => x.status === CONTENT_STATUS.DRAFT).length,
      pending: all.filter((x) => x.status === CONTENT_STATUS.REVIEW).length,
      published: all.filter((x) => x.status === CONTENT_STATUS.PUBLISHED).length,
    };
  }, [tips, challenges]);

  return (
    <DashboardLayout title="Inhalte">
      <div className="space-y-5">
        <section className="ac-soft-panel ac-content-hero">
          <div className="ac-content-hero-grid">
            <div>
              <h2 className="ac-content-hero-title">Inhalte Übersicht</h2>
              <p className="ac-content-hero-text">
                Hier verwaltest du Tipps und Challenges, prüfst Status und Qualität
                und bereitest neue Inhalte für die Veröffentlichung vor.
              </p>

              <div className="ac-content-chip-row">
                <Chip label="Tipps" value={loading ? "…" : stats.tipCount} />
                <Chip label="Challenges" value={loading ? "…" : stats.challengeCount} />
                <Chip label="Entwürfe" value={loading ? "…" : stats.drafts} />
                <Chip label="Zur Prüfung" value={loading ? "…" : stats.pending} />
                <Chip label="Veröffentlicht" value={loading ? "…" : stats.published} />
              </div>
            </div>

            <div className="ac-content-hero-side">
              <div className="ac-content-acebox">
                <AceAvatar variant="ruhig2" size={56} />
                <div className="ac-content-acebubble">
                  Ace sagt: Gute Inhalte sind kurz, klar und direkt hilfreich.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ac-content-main-grid">
          <Card
            title="Tipps verwalten"
            desc="Erstellen, bearbeiten, kategorisieren und veröffentlichen. Ideal für kurze, alltagstaugliche Hilfen."
            count={stats.tipCount}
            type="tips"
            tone="green"
            cta="Zur Tipps-Liste"
            onClick={() => navigate("/content/tips")}
          />

          <Card
            title="Challenges verwalten"
            desc="Challenges anlegen, Dauer sauber halten, Inhalte prüfen und Freigaben steuern."
            count={stats.challengeCount}
            type="challenges"
            tone="blue"
            cta="Zur Challenges-Liste"
            onClick={() => navigate("/content/challenges")}
          />
        </section>

        <section className="ac-soft-panel ac-content-block">
          <div className="ac-content-block-head">
            <div>
              <div className="ac-content-block-title">Schnellaktionen</div>
              <div className="ac-content-block-subtitle">
                Direkte Einstiege für neue Inhalte und offene Prüfungen
              </div>
            </div>

            <AceAvatar variant="motiviert2" size={42} />
          </div>

          <div className="ac-content-quickgrid">
            <QuickTile
              title="Neuer Tipp"
              sub="Direkt einen neuen Tipp als Entwurf anlegen"
              type="tips"
              onClick={() => navigate("/content/tips?new=1")}
            />

            <QuickTile
              title="Neue Challenge"
              sub="Neue Challenge als Entwurf vorbereiten"
              type="challenges"
              onClick={() => navigate("/content/challenges?new=1")}
            />

            <QuickTile
              title="Inhalte prüfen"
              sub={`${stats.pending} Inhalte warten aktuell auf Prüfung`}
              type="tips"
              onClick={() => navigate("/content/tips")}
            />
          </div>

          <div className="ac-content-categorybox">
            <div className="ac-content-block-title">Aktuelle Kategorien</div>
            <div className="ac-content-categoryrow">
              {CONTENT_CATEGORIES.map((cat) => (
                <span key={cat} className="ac-content-categorybadge">
                  {cat}
                </span>
              ))}
            </div>
          </div>  
        </section>
      </div>
    </DashboardLayout>
  );
}